import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { type MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { ConfirmDialogComponent } from '@dtis/angular';
import { TranslocoModule, translate } from '@jsverse/transloco';
import { dispatch, select } from '@ngxs/store';
import { AddView, DeleteView } from '../../app/states/multi-view/actions';
import { MultiViewState } from '../../app/states/multi-view/multi-view.state';

@Component({
  selector: 'dtis-views-drawer',
  host: {
    class: 'block flex-1 min-w-0 overflow-hidden',
  },
  imports: [CommonModule, RouterModule, MatButtonModule, MatIconModule, MatSidenavModule, TranslocoModule],
  template: `
    <mat-drawer-container class="h-full w-full flex-1 overflow-hidden" [hasBackdrop]="true">
      <mat-drawer #viewsDrawer mode="over" position="start" class="!w-80 max-w-full">
        <div class="flex flex-col gap-1 p-4">
          <h2 class="text-title-medium px-2 py-2">
            {{ 'LANDING.VIEW_LIST.views_title' | transloco }}
          </h2>

          @for (view of views(); track view.id + '-' + $index) {
            <div class="flex justify-between">
              <a
                mat-button
                class="flex! w-full justify-start!"
                data-cy="dtis-views-drawer-view-link"
                [routerLink]="['/databrowser', view.id]"
                routerLinkActive
                #viewLink="routerLinkActive"
                [class.bg-secondaryContainer]="viewLink.isActive"
                (click)="close()"
              >
                <mat-icon [class.!text-primary]="viewLink.isActive">show_chart</mat-icon>
                <span
                  class="text-body-medium ml-2 grow overflow-hidden text-ellipsis text-nowrap"
                  [class.!text-primary]="viewLink.isActive"
                  [class.text-onSurfaceVariant]="!viewLink.isActive"
                  >{{ view.label }}</span
                >
              </a>
              <button
                mat-icon-button
                type="button"
                data-cy="dtis-views-drawer-remove-view"
                [attr.aria-label]="'LANDING.VIEW_LIST.delete_view' | transloco"
                (click)="removeView($event, view.id)"
              >
                <mat-icon class="text-onSurfaceVariant!">close</mat-icon>
              </button>
            </div>
          } @empty {
            <p class="text-onSurfaceVariant text-body-small px-2">
              {{ 'LANDING.VIEW_LIST.no_views' | transloco }}
            </p>
          }

          <button
            mat-stroked-button
            data-cy="dtis-views-drawer-add-view"
            (click)="addView()"
            class="items-center"
          >
            <mat-icon>add</mat-icon>
            <span class="text-primary text-body-small">{{ 'LANDING.VIEW_LIST.add_new_view' | transloco }}</span>
          </button>
        </div>
      </mat-drawer>

      <mat-drawer-content class="flex h-full flex-col overflow-hidden flex-1">
        <ng-content></ng-content>
      </mat-drawer-content>
    </mat-drawer-container>
  `,
})
/**
 * Slide-over drawer for browsing, creating, and deleting saved views.
 *
 * It renders the list of available views from NGXS state and handles the
 * confirmation flow before permanently removing a saved view.
 */
export class DtisViewsDrawerComponent {
  @ViewChild('viewsDrawer') private readonly viewsDrawer?: MatDrawer;

  /** Selected list of saved views from the multi-view NGXS state. */
  views = select(MultiViewState.getViews);
  /** Dispatches a new view creation action. */
  addView = dispatch(AddView);
  /** Dispatches a view deletion action. */
  deleteView = dispatch(DeleteView);
  private readonly dialog = inject(MatDialog);

  /** Toggles the visibility of the drawer. */
  toggle() {
    this.viewsDrawer?.toggle();
  }

  /** Closes the drawer without changing the selected view. */
  close() {
    this.viewsDrawer?.close();
  }

  /**
   * Removes a saved view after confirmation from the user.
   *
   * @param event The click event that triggered the removal action.
   * @param id The unique identifier of the view to delete.
   */
  removeView(event: Event, id: string) {
    event.preventDefault();
    event.stopPropagation();

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: translate('LANDING.VIEW_LIST.delete_view'),
          message: translate('LANDING.VIEW_LIST.delete_view_confirm_message'),
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteView(id);
        }
      });
  }
}
